import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { z, ZodTypeAny } from "zod";
import isEqual from "lodash.isequal";
import { apiRequest } from "@core/services/api/apiRequest";
import { quizEventBus } from "../quiz.events";
import { packAnswerPayload } from "../utils/packAnswerPayload";
import { makeQuizQuestionInputKey } from "../utils/makeQuizQuestionInputKey";
import { packFormDefaultValuesFromQuiz } from "../utils/packFormDefaultValuesFromQuiz";
import { makeQuestionSetFormFields } from "../utils/makeQuestionSetFormFields";
import { filterFieldsByQuestionLogic } from "../utils/filterFieldsByQuestionLogic";
import type {
  QuizQuestion,
  QuizAnswerPayload,
  QuizResponse,
  QuizFormData,
  QuizQuestionSet,
  QuizQuestionInputType,
} from "../quiz.types";
import { AFFILIATION_ANSWER_TYPES } from "../quiz.types";

export type UseQuizNavigationArgs = {
  quiz: QuizResponse | undefined;
  quizResponseId?: number | undefined;
  resetIndexKey?: unknown;
  /** Called when quiz is completed. Receives all collected form data. */
  onCompleteQuiz: (formData: QuizFormData) => void;
  /** Optional callback after answer submission completes */
  onAnswerSubmitted?: () => void;
  /** When true, skips API calls and manages state locally only */
  anonymous?: boolean;
  /** Saves affiliation entries and returns their IDs for the quiz answer */
  onSaveAffiliations?: (entries: unknown[]) => Promise<number[]>;
};

export type UseQuizNavigationResult = {
  activeQuestionSet: QuizQuestionSet;
  buttonLabel: string;
  filterQuestionSet: (question: QuizQuestion, idx: number) => boolean;
  handleNextQuestionObject: (data: z.infer<ZodTypeAny>) => Promise<void>;
  handlePreviousQuestionObject: () => void;
  formData: QuizFormData;
  title: string;
  description: string;
  stepIndex: number;
  totalSteps: number;
  isSubmitting: boolean;
};

/**
 * Hook for managing quiz navigation and answer submission.
 * Handles stepping through question sets, submitting answers, and tracking progress.
 */
export function useQuizNavigation({
  quiz,
  quizResponseId,
  resetIndexKey,
  onCompleteQuiz,
  onAnswerSubmitted,
  anonymous = false,
  onSaveAffiliations,
}: UseQuizNavigationArgs): UseQuizNavigationResult {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isReady =
    !!quiz && Array.isArray(quiz.questionSets) && quiz.questionSets.length > 0;

  const isGatedLinear = isReady && quiz.type === "gated linear";
  const isLinear = isReady && quiz.type === "linear";
  const isSingleQuestionMode = isGatedLinear || isLinear;

  const defaultButtonLabel = isSingleQuestionMode ? "Yes, continue" : "Continue";

  const [buttonLabel, setButtonLabel] = useState(defaultButtonLabel);

  const [formData, setFormData] = useState<QuizFormData>({});

  const [currentQuestionObjectIndex, setCurrentQuestionObjectIndex] =
    useState(0);

  // For linear mode, flatten all question sets into one and track original headers
  type HeaderMapEntry = { header: string; description: string };
  const { mergedQuestionSet, headerMap } = useMemo((): {
    mergedQuestionSet: QuizQuestionSet | null;
    headerMap: Record<number, HeaderMapEntry>;
  } => {
    if (!isReady || !isLinear) {
      return { mergedQuestionSet: null, headerMap: {} };
    }

    const allQuestions: QuizQuestion[] = [];
    const map: Record<number, HeaderMapEntry> = {};

    quiz.questionSets.forEach((set) => {
      set.questions.forEach((question) => {
        const index = allQuestions.length;
        map[index] = {
          header: set.header,
          description: set.description || "",
        };
        allQuestions.push(question);
      });
    });

    return {
      mergedQuestionSet: {
        header: quiz.title || "",
        description: quiz.description || "",
        questions: allQuestions,
      } as QuizQuestionSet,
      headerMap: map,
    };
  }, [isReady, isLinear, quiz]);

  const questionObject = isReady
    ? isGatedLinear
      ? quiz.questionSets[0]
      : isLinear
        ? mergedQuestionSet
        : quiz.questionSets
    : [];

  const questions = isSingleQuestionMode
    ? (questionObject as QuizQuestionSet)?.questions ?? []
    : (questionObject as QuizQuestionSet[])?.[currentQuestionObjectIndex]
        ?.questions ?? [];

  // set form default data when quiz changes
  useEffect(() => {
    if (!quiz) return;
    if (!quiz.questions) return;

    setFormData(packFormDefaultValuesFromQuiz(quiz));
  }, [quiz]);

  useEffect(() => {
    setCurrentQuestionObjectIndex(0);
    setButtonLabel(defaultButtonLabel);
  }, [quiz, resetIndexKey, defaultButtonLabel]);

  /** Check if a question at the given index has any visible fields after conditional logic. */
  const hasVisibleFields = useCallback(
    (index: number, values: QuizFormData): boolean => {
      if (!isSingleQuestionMode) return true;

      const allQuestions =
        (questionObject as QuizQuestionSet)?.questions ?? [];
      const question = allQuestions[index];
      if (!question) return false;

      const fields = makeQuestionSetFormFields([question]);
      const visible = filterFieldsByQuestionLogic(fields, values);
      return visible.length > 0;
    },
    [isSingleQuestionMode, questionObject]
  );

  const mutation = useMutation({
    mutationFn: async (answerPayload: QuizAnswerPayload) => {
      return await apiRequest("/quiz/response/answer", "POST", answerPayload);
    },
    onError: () => {
      setIsSubmitting(false);
    },
    onSuccess: (data, variables) => {
      const { question, answer_text, selected_quiz_choices } = variables;

      const responseId = data.quiz_response.id;

      quizEventBus.emitEvent("quiz:answer:submitted", {
        questionId: question,
        answer: answer_text ?? selected_quiz_choices ?? [],
        quizId: quiz?.id ? Number(quiz.id) : 0,
        responseId,
      });
    },
  });

  const handleNextQuestionObject = async (data: z.infer<ZodTypeAny>) => {
    if (!isReady || !quiz) return;

    setIsSubmitting(true);

    const previousFormData = { ...formData };
    const updatedFormData = { ...formData, ...data };

    // Skip API calls in anonymous mode
    if (!anonymous && !isGatedLinear && quizResponseId !== undefined) {
      for (const [fieldName, answer] of Object.entries(data)) {
        const question = questions.find(
          (q) => makeQuizQuestionInputKey(q.id) === fieldName
        );

        if (!question) continue;

        // For affiliation questions, save affiliations first and use returned IDs
        const isAffiliationQuestion = AFFILIATION_ANSWER_TYPES.includes(
          question.type as QuizQuestionInputType
        );

        let answerValue = answer as string | number[];

        if (isAffiliationQuestion && onSaveAffiliations && Array.isArray(answer)) {
          const affiliationIds = await onSaveAffiliations(answer as unknown[]);
          answerValue = affiliationIds;
        }

        const answerPayload = packAnswerPayload({
          question,
          answer: answerValue,
          quizResponseId,
        });

        if (!isEqual(previousFormData[fieldName], answer) && answer !== "") {
          await mutation.mutateAsync(answerPayload);

          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      // Call optional callback after submission
      onAnswerSubmitted?.();
    }

    // Only update formData after successful API calls so retries re-attempt submission
    setFormData(updatedFormData);
    setIsSubmitting(false);

    const totalStepsCalc = Math.max(0, isSingleQuestionMode
      ? ((questionObject as QuizQuestionSet)?.questions?.length ?? 1) - 1
      : ((questionObject as QuizQuestionSet[])?.length ?? 1) - 1);

    // Find the next index with visible fields, skipping hidden questions
    let nextIndex = currentQuestionObjectIndex + 1;
    while (nextIndex <= totalStepsCalc && !hasVisibleFields(nextIndex, updatedFormData)) {
      nextIndex++;
    }

    if (nextIndex <= totalStepsCalc) {
      setTimeout(() => {
        setCurrentQuestionObjectIndex(nextIndex);
      }, 25);

      // Check if there are any visible questions after the next one
      let hasMoreVisible = false;
      for (let i = nextIndex + 1; i <= totalStepsCalc; i++) {
        if (hasVisibleFields(i, updatedFormData)) {
          hasMoreVisible = true;
          break;
        }
      }

      if (!hasMoreVisible) {
        setButtonLabel("Submit");
      }
    } else {
      onCompleteQuiz(updatedFormData);
    }
  };

  const handlePreviousQuestionObject = () => {
    // Find the previous index with visible fields, skipping hidden questions
    let prevIndex = currentQuestionObjectIndex - 1;
    while (prevIndex > 0 && !hasVisibleFields(prevIndex, formData)) {
      prevIndex--;
    }
    setCurrentQuestionObjectIndex(Math.max(0, prevIndex));
    setButtonLabel(defaultButtonLabel);
  };

  const filterQuestionSet = useCallback(
    (_question: QuizQuestion, idx: number) =>
      isSingleQuestionMode ? idx === currentQuestionObjectIndex : true,
    [currentQuestionObjectIndex, isSingleQuestionMode]
  );

  const activeQuestionSet = isSingleQuestionMode
    ? (questionObject as QuizQuestionSet)
    : (questionObject as QuizQuestionSet[])?.[currentQuestionObjectIndex] ?? {
        questions: [],
        header: "",
      };

  // For linear mode, look up the original header from the header map
  const currentHeaderMeta = isLinear ? headerMap[currentQuestionObjectIndex] : null;

  const title = isGatedLinear
    ? (questionObject as QuizQuestionSet)?.header || quiz?.title || ""
    : isLinear
      ? currentHeaderMeta?.header || quiz?.title || ""
      : activeQuestionSet?.header || quiz?.title || "";

  const description = isGatedLinear
    ? (questionObject as QuizQuestionSet)?.description || quiz?.description || ""
    : isLinear
      ? currentHeaderMeta?.description || quiz?.description || ""
      : activeQuestionSet?.description || quiz?.description || "";

  const stepIndex = currentQuestionObjectIndex;
  const totalSteps = isSingleQuestionMode
    ? (questionObject as QuizQuestionSet)?.questions?.length ?? 0
    : (questionObject as QuizQuestionSet[])?.length ?? 0;

  return {
    activeQuestionSet,
    buttonLabel,
    filterQuestionSet,
    handleNextQuestionObject,
    handlePreviousQuestionObject,
    formData,
    title,
    description,
    stepIndex,
    totalSteps,
    isSubmitting,
  };
}

